<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-contact-reasons-detail-metric-analysis" v-loading="pageLoading">
    <div :style="isAfterAvailableStartDate ? 'display: block' : 'display: none'">
      <div class="page-header">
        
        <el-tabs v-model="activeTabView" @tab-click="handleChangeSubPage">

          <el-tab-pane label="Bot Experience Score (BES)" name="experience">
            <div class="page-body" ref="pageBody">
                <wy-analytics-contact-reasons-detail-metric-analysis-table
                  ref="analysisTable"
                  :loading="tableLoading"
                  :parent="module"
                  activeSubPage="experience"
                  :tableData="tableData"
                  :topicDisplayName="topicDisplayName"
                />

                <wy-analytics-contact-reasons-detail-metric-analysis-chart ref="analysisChart" :loading="chartLoading" :parent="module" activeSubPage="experience" :chartData="chartData" />

                <wy-analytics-contact-reasons-detail-metric-analysis-transcripts
                  ref="analysisTranscriptsExperience"
                  :parent="module"
                  :tableData="tableData"
                  activeSubPage="experience"
                  :transcriptData="transcriptData"
                  :topicDisplayName="topicDisplayName"
                />
            </div>
          </el-tab-pane>

          <el-tab-pane label="Bot Automation Score (BAS)" name="automation">
            <div class="page-body" ref="pageBody">
                <wy-analytics-contact-reasons-detail-metric-analysis-table
                  ref="analysisTable"
                  :loading="tableLoading"
                  :parent="module"
                  activeSubPage="automation"
                  :tableData="tableData"
                  :topicDisplayName="topicDisplayName"
                />

                <wy-analytics-contact-reasons-detail-metric-analysis-chart ref="analysisChart" :loading="chartLoading" :parent="module" activeSubPage="automation" :chartData="chartData" />

                <wy-analytics-contact-reasons-detail-metric-analysis-transcripts
                  ref="analysisTranscriptsAutomation"
                  :parent="module"
                  :tableData="tableData"
                  activeSubPage="automation"
                  :transcriptData="transcriptData"
                  :topicDisplayName="topicDisplayName"
                />
            </div>
          </el-tab-pane>

        </el-tabs>
      </div>
    </div>

    <div class="no-data-analysis" :style="isAfterAvailableStartDate ? 'display: none' : 'display: block'">
      <div class="no-data-icon"></div>
      <div class="no-data-title">Sorry!</div>
      <div class="no-data-subtitle">No data available in the brand, channel or more filters.</div>
      <div class="no-data-subtitle">Try adjusting the global filter to locate available data.</div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import WyUtils from '../../library/wy-utils';
import moment from 'moment';
import WyAnalyticsContactReasonsDetailMetricAnalysisTable from './wy-analytics-contact-reasons-detail-metric-analysis-table.vue';
import WyAnalyticsContactReasonsDetailMetricAnalysisChart from './wy-analytics-contact-reasons-detail-metric-analysis-chart.vue';
import WyAnalyticsContactReasonsDetailMetricAnalysisTranscripts from './wy-analytics-contact-reasons-detail-metric-analysis-transcripts';

const PanelNameEnum = Object.freeze({
  TopicCXBreakdown: 'topicCXBreakdown',
  SignalDistribution: 'signalDistribution',
});

export const TabNameEnum = Object.freeze({
  EXPERIENCE: 'experience',
  AUTOMATION: 'automation',
});

export const DropdownOptionsEnum = Object.freeze({
  BES: 'BES Signals',
  BAS: 'BAS Signals',
});

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-metric-analysis',
  props: ['parent', 'activeTab'],
  components: {
    'wy-analytics-contact-reasons-detail-metric-analysis-table': WyAnalyticsContactReasonsDetailMetricAnalysisTable,
    'wy-analytics-contact-reasons-detail-metric-analysis-chart': WyAnalyticsContactReasonsDetailMetricAnalysisChart,
    'wy-analytics-contact-reasons-detail-metric-analysis-transcripts': WyAnalyticsContactReasonsDetailMetricAnalysisTranscripts,
  },

  data() {
    return {
      module: this,
      fetchData: true,
      initDate: null,
      isAfterAvailableStartDate: null,
      topicDisplayName: this.parent && this.parent.currentItem && this.parent.currentItem.topicDisplayName ? this.parent.currentItem.topicDisplayName : '',

      selectedTopic: null,

      pageLoading: false,
      tableLoading: false,
      chartLoading: false,

      preMsgObj: null,

      tableData: [],
      chartData: [],
      transcriptData: [],

      activeTabView: TabNameEnum.EXPERIENCE,

      parentCurrentItem: this.parent && this.parent.currentItem ? this.parent.currentItem : null,

      subPages: [{ name: TabNameEnum.EXPERIENCE, show: true }, { name: TabNameEnum.AUTOMATION, show: false }],
      activeSubPage: TabNameEnum.EXPERIENCE,

      breakdownColorScheme: {
        Escalation: '#D65858',
        'Bot Repetition': '#D65858',
        'User Abandoned': '#86D3FE',
        'Escalation not Connected': '#F8E4A2',
        'Negative Sentiment': '#F8E4A2',
        'False Positives': '#E5996D',
        'Repeated Escalation Requests': '#E5996D',
        'Negative Feedback': '#4B67A3',
        'Bad Containment': '#5FBBAA',
        Profanity: '#5FBBAA',
        'User Paraphrases': '#AE69CE',
      },
    };
  },

  computed: {
    ...mapGetters(['transcriptInfoReference', 'companyConfig', 'config', 'dataModel']),
  },

  watch: {
    async activeTab() {
      const analysisTranscriptsRef = this.activeTabView == 'experience' ? this.$refs.analysisTranscriptsExperience : this.$refs.analysisTranscriptsAutomation;
      if (analysisTranscriptsRef) {
        analysisTranscriptsRef.handleCancelAdvancedSearch()
      }
    },
    selectedTopic() {
      if (this.selectedTopic) {
        if (this.selectedTopic.avgBes === 100) {
          this.activeSubPage = TabNameEnum.AUTOMATION;
        }
        if (this.activeSubPage == TabNameEnum.AUTOMATION && this.selectedTopic.avgBas == 100) {
          this.activeSubPage = TabNameEnum.EXPERIENCE;
        }
        if (this.activeSubPage == TabNameEnum.EXPERIENCE && this.selectedTopic.avgBes == 100) {
          this.activeSubPage = TabNameEnum.AUTOMATION;
        }
      }
    },
    async activeTabView() {
      this.activeSubPage = this.activeTabView;
    },
  },

  async created() {
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if (this[event.name]) this[event.name]();
    });
  },

  methods: {
    ...mapActions(['fetchContactReasonsTopicCxBreakdown']),
    ...mapMutations(['updateTranscriptInfoReference']),

    // called by parent
    startLoading() {
      // edge case where BES is 100, then we default select to BAS
      if (this.parentCurrentItem.avgBes == 100) {
        this.activeSubPage = TabNameEnum.AUTOMATION;
      }
      this.selectedTopic = this.parentCurrentItem;
      this.initDate = new Date();
      if (!this.loading) {
        if (this.initDate) {
          this.$store.dispatch('amplitudeLogWithFilters', {
            name: 'metricAnalysisTopics_loaded',
            data: { topic_name: this.parent.currentItem.topicDisplayName, data_load_time: WyUtils.getTimeDiff(this.initDate) },
          });
          this.initDate = null;
        }
      }
      this.loadMetricAnalysis(this.parentCurrentItem);
    },

    async handleChangeSubPage() {
      const tabMap = {
        "experience": "BES",
        "automation": "BAS"
      }
      const eventName = tabMap[this.activeTabView];

      if (this.activeSubPage != this.activeTabView) {
        this.subPages.forEach((section) => {
          if (section.name === this.activeTabView) {
            section.show = true;
          } else {
            section.show = false;
          }
        });

        this.$router.logAmplitudeEvent({ eventName: `CA_CR_Metrics_Analysis: ${eventName} Filter Clicked` });
      }

      this.loadMetricAnalysisChart(this.selectedTopic);
      const analysisTranscriptsRef = this.activeTabView == 'experience' ? this.$refs.analysisTranscriptsExperience : this.$refs.analysisTranscriptsAutomation;
      if (analysisTranscriptsRef) {
        await analysisTranscriptsRef.renderPageLogic(this.activeTabView);
        this.handleShowMetricAnalysis();
      }
      return;
    },

    async loadMetricAnalysis(selectedTopic) {
      this.selectedTopic = selectedTopic;
      if (this.preMsgObj) {
        this.preMsgObj.visible = false;
        this.preMsgObj.onClose();
      }
      if (this.companyConfig && this.companyConfig.api && this.companyConfig.api.configs && this.companyConfig.api.configs.firstAvailableDateOfBesBasSignalBreakdown) {
        let dateStart = this.companyConfig.api.configs.firstAvailableDateOfBesBasSignalBreakdown;
        if (moment(this.dataModel.dateStart) < moment(dateStart)) {
          if (!this.preMsgObj) {
            this.preMsgObj = this.$message({
              dangerouslyUseHTMLString: true,
              duration: 50000000,
              showClose: true,
              customClass: 'no-data-msg',
              message: `
                <div class="first-div">Data is not available for the date range: ${moment(this.dataModel.dateStart, 'YYYY-MM-DD').format('MMM Do, YYYY')} to ${moment(
                this.dataModel.dateEnd,
                'YYYY-MM-DD'
              ).format('MMM Do, YYYY')}</div> 
                <div class="second-div">To view data, please modify date range to start from ${moment(dateStart, 'YYYY-MM-DD').format('MMM Do, YYYY')}</div>`,
              onClose: () => {
                this.preMsgObj = null;
              },
            });
            const eventInfo = {
              alert_name: 'Data Not Available for Date Range',
            };
            this.$router.logAmplitudeEvent({ eventName: 'alert_popup', eventInfo });
            setTimeout(() => {
              let leftMenuEl = document.querySelector('.wy-container-nav');
              let leftMenuElWidth = 0;
              if (leftMenuEl) {
                leftMenuElWidth = leftMenuEl.getBoundingClientRect().width;
              }
              this.preMsgObj.$el.style.left = this.preMsgObj.$el.offsetLeft + leftMenuElWidth / 2 + 'px';
            }, 0);

            this.$root.preMsgObj = this.preMsgObj;
          }
        }
      }
      if (!this.fetchData) return;
      if (this.$refs.analysisChart && this.$refs.analysisChart.handleCreateViewByOptions) {
        this.$refs.analysisChart.handleCreateViewByOptions();
      }
      const analysisTranscriptsRef = this.activeTabView == 'experience' ? this.$refs.analysisTranscriptsExperience : this.$refs.analysisTranscriptsAutomation;
      this.pageLoading = true;

      const metricAnalysisPane = document.querySelector('.wy-analytics-contact-reasons-detail .wy-page-tab #pane-metricAnalysis');
      metricAnalysisPane.scrollTop = 0;
      metricAnalysisPane.style.overflowY = 'hidden';      

      // cannot use promise.all due to network caching of fetchContactReasonsTopicCxBreakdown
      await this.loadMetricAnalysisTable(selectedTopic),
      await this.loadMetricAnalysisChart(selectedTopic)
      if (analysisTranscriptsRef) {
        await (analysisTranscriptsRef.loadTranscriptData());
      }

      this.pageLoading = false;
      this.fetchData = false;

      metricAnalysisPane.style.overflowY = 'auto';
    },

    async loadMetricAnalysisTable(selectedTopic) {
      this.tableLoading = true;
      const topicCXBreakdownData = await this.fetchContactReasonsTopicCxBreakdown({
        topicDisplayName: this.topicDisplayName,
        panelName: PanelNameEnum.TopicCXBreakdown,
        besScore: selectedTopic ? selectedTopic.avgBes : this.parent.currentItem.avgBes,
        basScore: selectedTopic ? selectedTopic.avgBas : this.parent.currentItem.avgBas,
      });

      if (topicCXBreakdownData && topicCXBreakdownData.status === 200) {
        this.tableData = topicCXBreakdownData.data;
      }
      this.tableLoading = false;
    },

    async loadMetricAnalysisChart(selectedTopic) {
      const analyticsChartRef = this.$refs.analysisChart;
      if (!analyticsChartRef) return;

      this.chartLoading = true;
      analyticsChartRef.showChart = true;
      const signalBreakdownData = await this.fetchContactReasonsTopicCxBreakdown({
        topicDisplayName: this.topicDisplayName,
        panelName: PanelNameEnum.SignalDistribution,
        besScore: selectedTopic ? selectedTopic.avgBes : this.parent.currentItem.avgBes,
        basScore: selectedTopic ? selectedTopic.avgBas : this.parent.currentItem.avgBas,
        viewBy: analyticsChartRef.signalDropdownViewBy,
      });

      if (signalBreakdownData && signalBreakdownData.status === 200) {
        // prepare mock data for chart testing
        // this.mockData(signalBreakdownData.data);
        this.formatDataWithViewBy(signalBreakdownData);
        this.chartData = signalBreakdownData.data;
        if ((this.chartData && this.chartData.bes && this.chartData.bes.length) || (this.chartData && this.chartData.bas && this.chartData.bas.length)) {
          analyticsChartRef.showChart = true;
        } else {
          analyticsChartRef.showChart = false;
        }
      }
      this.chartLoading = false;
    },

    formatDataWithViewBy(signalBreakdownData) {
      const besData = signalBreakdownData.data.bes;
      const basData = signalBreakdownData.data.bas;

      const signalBesDataExists = besData && besData.length;
      const signalBasDataExists = basData && basData.length;
      const analyticsChartRef = this.$refs.analysisChart;

      if (signalBesDataExists) {
        signalBreakdownData.data.bes.forEach((bes) => {
          bes.data.forEach((item) => {
            if (analyticsChartRef.signalDropdownViewBy == 'day') {
              item.date = moment(item.date).format('MMM DD, YYYY');
            } else if (analyticsChartRef.signalDropdownViewBy == 'week') {
              item.date = moment(item.startDate).format('MMM DD, YYYY');
            } else if (analyticsChartRef.signalDropdownViewBy == 'month') {
              item.date = moment(item.date).format('MMM, YYYY');
            } else if (analyticsChartRef.signalDropdownViewBy == 'quarter') {
              let year = item.date.split('-')[0];
              let quarter = item.date.split('-')[1];
              let quarterLabel = 'Q' + quarter + ' ' + year;
              item.date = quarterLabel;
            } else if (analyticsChartRef.signalDropdownViewBy == 'year') {
              item.date = moment(item.date).format('YYYY');
            }
          });
        });
      }
      if (signalBasDataExists) {
        signalBreakdownData.data.bas.forEach((bas) => {
          bas.data.forEach((item) => {
            if (analyticsChartRef.signalDropdownViewBy == 'day') {
              item.date = moment(item.date).format('MMM DD, YYYY');
            } else if (analyticsChartRef.signalDropdownViewBy == 'week') {
              item.date = moment(item.startDate).format('MMM DD, YYYY');
            } else if (analyticsChartRef.signalDropdownViewBy == 'month') {
              item.date = moment(item.date).format('MMM, YYYY');
            } else if (analyticsChartRef.signalDropdownViewBy == 'quarter') {
              let year = item.date.split('-')[0];
              let quarter = item.date.split('-')[1];
              let quarterLabel = 'Q' + quarter + ' ' + year;
              item.date = quarterLabel;
            } else if (analyticsChartRef.signalDropdownViewBy == 'year') {
              item.date = moment(item.date).format('YYYY');
            }
          });
        });
      }
    },

    mockData(data) {
      if (data.bes && data.bes.length) {
        data.bes.forEach((bes) => {
          bes.data.forEach((item) => {
            item.value = Math.floor(Math.random() * 1000);
          });
        });
      }
      if (data.bas && data.bas.length) {
        data.bas.forEach((bas) => {
          bas.data.forEach((item) => {
            item.value = Math.floor(Math.random() * 1000);
          });
        });
      }
    },

    advancedFilterHideEvent(e) {
      const ref = this.activeTabView == 'experience' ? this.$refs.analysisTranscriptsExperience : this.$refs.analysisTranscriptsAutomation;
      const refMethodExists = ref && ref.handleEventsOutsideOfPopover;
      const refPopoverIsShowing = ref && ref.showAdvancedSearchFilter;

      if (refMethodExists && refPopoverIsShowing) {
        const targetClassList = e && e.target && e.target.classList;
        const isSearchInputClicked = ['search-inp', 'search-input-field', 'el-input', 'el-input__inner', 'advanced-search-icon', 'advanced-metric-search-filter-tag-advanced'].some((className) => targetClassList && targetClassList.contains(className));
        const isSearchInputUndefined = targetClassList === undefined;

        if (isSearchInputClicked || isSearchInputUndefined) {
          ref.showAdvancedSearchFilter = true;
          return;
        } else {
          ref.handleEventsOutsideOfPopover();
        }
      }
    },

    handleTranscriptPopover(e) {
      if (this.transcriptInfoReference) {
        this.transcriptInfoReference.visible = false;
        this.updateTranscriptInfoReference(null);
      }

      this.advancedFilterHideEvent(e);
    },



    handleShowMetricAnalysis() {
      // If dataModel dateStart is after the firstAvailableDateOfBesBasSignalBreakdown, then we can show the table
      // otherwise we show the no available date message
      const dateStart = this.dataModel && this.dataModel.dateStart;
      const companyConfigValue = (this.companyConfig && this.companyConfig.api && this.companyConfig.api.configs && this.companyConfig.api.configs.firstAvailableDateOfBesBasSignalBreakdown) || null;
      const globalConfigValue = (this.config && this.config.api && this.config.api.configs && this.config.api.configs.firstAvailableDateOfBesBasSignalBreakdown) || null;

      if (companyConfigValue) {
        this.isAfterAvailableStartDate = moment(dateStart) >= moment(companyConfigValue);
      } else if (globalConfigValue) {
        this.isAfterAvailableStartDate = moment(dateStart) >= moment(globalConfigValue);
      } else {
        this.isAfterAvailableStartDate = false;
      }
    },
  },

  async mounted() {
    this.handleShowMetricAnalysis();
    this.$root.$on(`fire-global-filter-change-events`, async () => {
      this.handleShowMetricAnalysis();
    });

    this.$refs.pageBody &&
      this.$refs.pageBody.addEventListener('scroll', () => {
        if (document.querySelector('.el-dropdown-menu.el-popper.signal-content-wrapper')) {
          document.querySelector('.el-dropdown-menu.el-popper.signal-content-wrapper').remove();
        }
      });

    // on resize window, we close the transcript popover
    window.addEventListener('resize', (e) => {
      this.handleTranscriptPopover(e)
    });

    // on scroll, we close the transcript popover & any opened dropdowns
    document.querySelector('.wy-analytics-contact-reasons-detail .wy-page-tab #pane-metricAnalysis').addEventListener('scroll', (e) => {
      this.handleTranscriptPopover(e);

      const analysisTranscriptsRef = this.activeTabView == 'experience' ? this.$refs.analysisTranscriptsExperience : this.$refs.analysisTranscriptsAutomation;
      if (this.$refs.analysisChart.signalDropdown.opened) {
        this.$refs.analysisChart.$refs.signalDropdown.handleClick();
      }
      if (document.querySelector('.el-select-dropdown.el-popper.transcript-filter-dropdown')) {
        document.querySelector('.el-select-dropdown.el-popper.transcript-filter-dropdown').remove();
      }
      if (document.querySelector('.el-select-dropdown.el-popper.contact-reasons-picker-dropdown')) {
        document.querySelector('.el-select-dropdown.el-popper.contact-reasons-picker-dropdown').remove();
      }
      if (analysisTranscriptsRef && analysisTranscriptsRef.transcriptDropdowns) {
        analysisTranscriptsRef.closeAllDropdowns();
      }
    });

    // Outside of metric-analysis
    document.addEventListener('click', (e) => {
      this.handleTranscriptPopover(e);
    });

    document.querySelector('.wy-page-title').addEventListener('click', (e) => {
      this.handleTranscriptPopover(e);
    });

    // Inside metric-analysis
    document.querySelector('.wy-analytics-contact-reasons-detail-metric-analysis').addEventListener('click', (e) => {
      this.handleTranscriptPopover(e);
    });
  },
});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-metric-analysis {
  display: flex;
  flex-direction: column;

  font-size: 14.22px;
  font-family: 'Helvetica Neue', sans-serif !important;

  height: calc(100vh - 320px) !important;

  .el-tabs__nav-scroll {
    display: flex;
    margin-bottom: 20px;
    border-bottom: none !important;
  }
  .el-tabs__item {
    width: 300px !important;
    border: 1px solid #cad2da !important;
    padding-left: 18px !important;
    padding-right: 54px !important;
    border-radius: 20px !important;
    height: 24px !important;
    line-height: 17px !important;
    font-size: 14px !important;
    font-family: 'Helvetica Neue', sans-serif !important;

    &:hover {
      border-color: #c6e2ff !important;
      background-color: #ecf5ff !important;
    }

    &.is-active {
      background-color: white!important;
      border: 1px solid #1a55ad !important;
      color: black !important;
      font-family: 'Helvetica Neue', sans-serif !important;
    }
  }

  .page-header {
    border-bottom: 1px solid #cad2da;
    padding-bottom: 25px;

    .subPage-button {
      padding: 8px 16px;
      color: #202223;
      font-weight: 400;

      &.active {
        border: 1px solid #1a55ad;
        background-color: white;
      }
    }
    // remove border from el-tabs
    .el-tabs__content {
      border: none !important;
    }


  }

  .page-body {
    padding: 25px 10px 0px 0px;
    color: var(--site-font-chart-color);

    overflow-x: hidden;
    overflow-y: hidden;

    .wy-page-modal {
      height: 100%;
    }
    .wy-analytics-transcript-search-expand {
      height: 90%;
    }

    // &::-webkit-scrollbar {
    //   width: 20px;
    // }
    // &::-webkit-scrollbar-thumb {
    //   border-radius: 160px;
    //   border: 3px solid transparent;
    //   border-top: 5px solid transparent;
    //   background-clip: content-box;
    //   background-color: #cad2da;
    // }
    // &::-webkit-scrollbar-track {
    //   background: #f3f6f8;
    //   border-top: 1px solid #cad2da;
    // }

    // // Vertical
    // &::-webkit-scrollbar:vertical {
    //   width: 14px;
    // }
    // &::-webkit-scrollbar-thumb:vertical {
    //   border-radius: 160px;
    //   border: 2px solid transparent;
    //   border-left: 3px solid transparent;
    //   border-right: 3px solid transparent;
    //   background-clip: content-box;
    //   background-color: #cad2da;
    // }
    // &::-webkit-scrollbar-track:vertical {
    //   border-top: none !important;
    // }
  }
  .no-data-analysis {
    margin-top: 8%;
    text-align: center;
    color: var(--site-font-tertiary-color);
    font-family: Helvetica Neue, Helvetica, Arial, sans-serif !important;

    .no-data-icon {
      display: block;
      border: none;
      height: 155px;
      background-position-x: 50%;
      background-repeat: no-repeat;
      background-image: url('../../assets/icons/icon_no_data_new.svg');
      margin-bottom: 18px;
    }

    .no-data-title {
      font-weight: 500;
      font-size: 18px;
      margin-bottom: 18px;
    }

    .no-data-subtitle {
      font-weight: 500;
      font-size: 14.5px;
      margin-bottom: 2px;
      color: #4b5158;
    }
  }
  .el-tabs__active-bar {
    height: 0px !important;
  }
  .el-tabs__item {
    margin-right: 10px !important;
  }
}
</style>
